<!--
  AdminOtpInput.vue

  Step 2 of the admin fallback gate (D5/D6 of
  docs/exec-plans/completed/2026-06-23-admin-otp-gate-validateSecurityPermissionNoPrompt.md,
  mirrored from Retail POS into Restaurant POS).

  Renders a 5-digit OTP input with a 30s resend cooldown. The operator enters
  the OTP they received (via SMS / push) and the component:
    1. Calls `supportStaffGate.verifyOtp(otp)` to validate with the ticket API.
    2. On success emits `'verified'` with the verified username — the parent
       `AdminSupportStaffModal` records it and resolves the gate promise as
       `true`.
    3. On failure (or after 3 wrong attempts) emits `'failed'` — the parent
       decides whether to allow another attempt or auto-close the modal.

  This mirrors the OTP verification behaviour in
  `src/components/login/steps/OtpVerificationStep.vue` (5 digits, auto-submit
  on length, 30s resend cooldown) but adds the 3-attempt cap required by the
  gate spec.
-->
<template>
  <div class="admin-otp-step">
    <h4 class="card-title mb-3 text-uppercase">
      <span class="gradient-text">Enter</span>
      <span class="text-primary-color">OTP</span>
    </h4>
    <p class="text-secondary mb-4 small">
      Enter the 5-digit code sent to
      <strong>{{ selectedUser && (selectedUser.name || selectedUser.username || selectedUser.email) }}</strong>.
      Attempts remaining: <strong>{{ attemptsRemaining }}</strong>.
    </p>

    <form @submit.prevent="handleSubmit">
      <div class="form-floating mb-3">
        <input
          type="text"
          class="form-control custom-input"
          v-model="otp"
          id="adminGateOtpInput"
          placeholder="Enter OTP"
          maxlength="5"
          inputmode="numeric"
          pattern="[0-9]*"
          required
          :disabled="loading"
        />
        <label for="adminGateOtpInput">Enter 5-digit OTP</label>
      </div>

      <p class="error-message" v-if="errorMessage">{{ errorMessage }}</p>

      <div class="d-flex gap-2 justify-content-center mt-3">
        <button
          type="button"
          class="btn btn-outline-secondary"
          @click="$emit('cancel')"
          :disabled="loading"
        >
          Cancel
        </button>
        <button
          type="submit"
          class="btn btn-primary"
          :disabled="loading || otp.length < 5"
        >
          <span class="button-text">{{ loadText }}</span>
          <span
            v-if="loading"
            class="spinner-grow spinner-grow-sm"
            role="status"
            aria-hidden="true"
          ></span>
        </button>
      </div>

      <button
        type="button"
        class="btn btn-link resend-button"
        :disabled="resendDisabled || resendLoading"
        @click="resendOtp"
      >
        Resend OTP
        <span
          v-if="resendLoading"
          class="spinner-grow spinner-grow-sm"
          role="status"
          aria-hidden="true"
        ></span>
      </button>
      <p class="timer-text" v-if="resendTimer > 0">
        You can resend OTP in {{ resendTimer }} seconds.
      </p>
    </form>
  </div>
</template>

<script>
import { supportStaffGate } from '@/services/supportStaffGate';

const MAX_ATTEMPTS = 3;
const RESEND_COOLDOWN_SECONDS = 30;

export default {
  name: 'AdminOtpInput',
  props: {
    /**
     * The support staff the operator picked in Step 1. Used purely for the
     * "sent to X" copy — verification itself uses the encrypted
     * `localStorage.users` envelope set by `supportStaffGate.requestOtp`.
     */
    selectedUser: { type: Object, default: null },
  },
  emits: ['verified', 'failed', 'cancel'],
  data() {
    return {
      otp: '',
      loading: false,
      errorMessage: '',
      attemptsRemaining: MAX_ATTEMPTS,
      resendLoading: false,
      resendDisabled: false,
      resendTimer: 0,
      resendInterval: null,
    };
  },
  computed: {
    loadText() {
      return this.loading ? 'Verifying...' : 'Submit';
    },
  },
  watch: {
    /**
     * Auto-submit once the operator has typed 5 characters — same UX as the
     * existing `OtpVerificationStep.vue` so the gate feels familiar.
     */
    otp(newVal) {
      if (newVal.length === 5 && !this.loading) {
        this.handleSubmit();
      }
    },
  },
  beforeDestroy() {
    if (this.resendInterval) clearInterval(this.resendInterval);
  },
  methods: {
    async handleSubmit() {
      if (this.otp.length !== 5) {
        this.errorMessage = 'Please enter a 5-digit OTP';
        return;
      }
      this.loading = true;
      this.errorMessage = '';
      try {
        const { username } = await supportStaffGate.verifyOtp(this.otp);
        this.$emit('verified', { username });
      } catch (error) {
        const msg =
          (error && error.response && error.response.data && error.response.data.message) ||
          'Failed to verify OTP. Please try again.';
        this.errorMessage = msg;
        this.attemptsRemaining -= 1;
        console.error('[AdminOtpInput] verifyOtp failed:', error);
        if (this.attemptsRemaining <= 0) {
          this.$emit('failed', msg);
        }
      } finally {
        this.loading = false;
        this.otp = '';
      }
    },

    async resendOtp() {
      this.resendLoading = true;
      this.errorMessage = '';
      try {
        await supportStaffGate.resendOtp();
        this.startResendCooldown();
      } catch (error) {
        this.errorMessage =
          (error && error.response && error.response.data && error.response.data.message) ||
          'Failed to resend OTP. Please try again.';
        console.error('[AdminOtpInput] resendOtp failed:', error);
      } finally {
        this.resendLoading = false;
      }
    },

    startResendCooldown() {
      this.resendDisabled = true;
      this.resendTimer = RESEND_COOLDOWN_SECONDS;
      if (this.resendInterval) clearInterval(this.resendInterval);
      this.resendInterval = setInterval(() => {
        this.resendTimer -= 1;
        if (this.resendTimer <= 0) {
          clearInterval(this.resendInterval);
          this.resendInterval = null;
          this.resendDisabled = false;
        }
      }, 1000);
    },
  },
};
</script>

<style lang="scss" scoped>
.admin-otp-step {
  padding: 0.5rem 0;
}
.card-title {
  font-weight: 700;
  letter-spacing: 1.2px;
  text-align: center;
  font-size: 1.4rem;
}
.text-primary-color {
  color: var(--text-primary);
}
.gradient-text {
  background: linear-gradient(to right, var(--accent-electric-blue), var(--accent-neon-purple), var(--accent-electric-blue));
  background-size: 200% 100%;
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}
.custom-input {
  border: var(--card-border);
  background-color: var(--card-bg);
  color: var(--text-primary);
  border-radius: 12px;
  padding: 0.75rem 1rem;
  height: 56px;
  font-size: 1rem;
  text-align: center;
  letter-spacing: 0.5em;
  font-weight: 600;
  &:focus {
    border-color: var(--accent-electric-blue);
    outline: none;
    box-shadow: 0 0 0 3px rgba(51, 156, 255, 0.25);
  }
}
.form-floating label {
  padding: 0.75rem 1rem;
  color: var(--text-secondary);
}
.error-message {
  color: var(--accent-danger, #ff3b30);
  font-size: 0.9rem;
  min-height: 20px;
  margin-bottom: 0.5rem;
  font-weight: 500;
}
.btn-primary {
  min-width: 140px;
  background: linear-gradient(to right, var(--accent-electric-blue), var(--accent-neon-purple));
  color: white;
  border: none;
  font-weight: 600;
  &:hover:not(:disabled) {
    transform: translateY(-1px);
  }
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
  .spinner-grow {
    margin-left: 8px;
  }
}
.resend-button {
  margin-top: 0.5rem;
  font-weight: 500;
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  .spinner-grow {
    margin-left: 8.5rem;
    vertical-align: middle;
  }
}
.timer-text {
  margin-top: 0.25rem;
  color: var(--text-secondary);
  font-size: 0.85rem;
}
</style>